import {
  Badge,
  Checkbox,
  Group,
  Select,
  TextInput,
  Modal,
  ActionIcon,
  Stack,
  Center,
  Button,
  Text,
  Anchor,
} from "@mantine/core";
import { Map, Marker } from "@vis.gl/react-google-maps";
import { UseFormReturnType } from "@mantine/form";
import { DeskVenueAssist, EventFormValues } from "./types";
import { Database } from "types/supabase";
import { isLocationFormatValid } from "utils";
import {
  IconQuestionMark,
  IconExternalLink,
  IconMapSearch,
} from "@tabler/icons-react";
import { useState, useEffect, useRef } from "react";
import { findClosestVenueMatch } from "utils/venues";
import { CitySelect } from "components/cities";

/** The map centre when there is no location and no desk fallback. */
const DEFAULT_MAP_CENTER = { lat: 37.8022298161398, lng: -8.676765624229938 };

interface EventFormVenueProps {
  form: UseFormReturnType<EventFormValues>;
  t: (key: string, options?: Record<string, unknown>) => string;
  customVenueKeyword: string;
  availableVenues: Database["public"]["Tables"]["venues"]["Row"][];
  handlePaste: (
    e: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>,
    field: keyof EventFormValues
  ) => void;
  venues: Database["public"]["Tables"]["venues"]["Row"][];
  applicationVenueLocation?: {
    venue_name: string;
    venue_location: string;
    venue_address: string;
    venue_city: string;
    venue_description: string;
    venue_phone: string;
    venue_website: string;
  };
  /**
   * Desk mode: skip the application-info auto-fill entirely, including the
   * AI matcher that posts the venue list to the paid PROD `openai` function
   * (Pitfall 5). The desk prefills the venue from the row instead.
   */
  disableAiMatch?: boolean;
  /**
   * Desk mode (33.3 D-07): where the map centres while the location is empty
   * or invalid, i.e. the town. Never written into `venue_location`. Absent
   * everywhere else (incl. /applications/:id), which keep the default centre.
   */
  mapFallbackCenter?: { lat: number; lng: number } | null;
  /**
   * Desk mode (33.3 D-04 / D-10): a translated line under the location field,
   * the "different places" note or the lookup-failed message. Absent
   * everywhere else.
   */
  locationNote?: string | null;
  /**
   * Desk mode (33.3 D-06): when given, the custom-venue section shows "Save as
   * venue"; the click goes up to EventFormMain, which owns the modal. Absent
   * everywhere else, which never show the button.
   */
  onSaveAsVenue?: () => void;
  /**
   * Desk mode (UAT 2026-11-02): listed venues of the form's town whose name
   * resembles the typed one. Each shows as "Use <venue>"; the click goes up to
   * EventFormMain, which selects it. Absent everywhere else.
   */
  similarVenues?: { id: number; name: string }[];
  onUseVenue?: (venueId: number) => void;
  /**
   * Desk mode (33.4 D-14 / D-24, P-09): the "Suggested venue: check it" badge
   * and the "Remember '<text>' as <venue>" checkbox, shown directly under the
   * venue field. Absent everywhere else, which show neither.
   */
  venueAssist?: DeskVenueAssist | null;
}

export const EventFormVenue = ({
  form,
  t,
  customVenueKeyword,
  availableVenues,
  handlePaste,
  venues,
  applicationVenueLocation,
  disableAiMatch = false,
  mapFallbackCenter = null,
  locationNote = null,
  onSaveAsVenue,
  similarVenues = [],
  onUseVenue,
  venueAssist = null,
}: EventFormVenueProps) => {
  const [showLocationHelp, setShowLocationHelp] = useState(false);
  const [findingVenue, setFindingVenue] = useState(false);
  const [foundVenue, setFoundVenue] = useState<
    Database["public"]["Tables"]["venues"]["Row"] | null
  >(null);
  const [userHasEdited, setUserHasEdited] = useState(false);
  const [initialSetupDone, setInitialSetupDone] = useState(false);

  const searchedVenueRef = useRef<string | null>(null);
  const lastAISuggestionRef = useRef<string | null>(null);

  useEffect(() => {
    // Before any call: the desk must never reach the prod matcher.
    if (disableAiMatch) {
      return;
    }
    if (
      !applicationVenueLocation ||
      venues.length === 0 ||
      searchedVenueRef.current === applicationVenueLocation.venue_name ||
      userHasEdited
    ) {
      return;
    }

    if (lastAISuggestionRef.current !== applicationVenueLocation.venue_name) {
      setUserHasEdited(false);
      lastAISuggestionRef.current = applicationVenueLocation.venue_name;
    }

    searchedVenueRef.current = applicationVenueLocation.venue_name;
    setFindingVenue(true);

    const exactMatch = venues.find(
      (venue) =>
        venue.name?.toLowerCase() ===
        applicationVenueLocation.venue_name?.toLowerCase()
    );

    if (exactMatch) {
      setFoundVenue(exactMatch);
      setFindingVenue(false);
    } else {
      findClosestVenueMatch(applicationVenueLocation.venue_name, venues)
        .then((match) => {
          setFoundVenue(match);
        })
        .catch((error) => {
          console.error("Error finding venue match:", error);
        })
        .finally(() => {
          setFindingVenue(false);
        });
    }
  }, [applicationVenueLocation, venues, userHasEdited, disableAiMatch]);

  useEffect(() => {
    if (foundVenue && !userHasEdited && !initialSetupDone) {
      form.setFieldValue("venue", foundVenue.name || "");
      setInitialSetupDone(true);
    }
  }, [foundVenue, form, userHasEdited, initialSetupDone]);

  useEffect(() => {
    if (disableAiMatch) {
      return;
    }
    if (applicationVenueLocation && !initialSetupDone) {
      // Only reset user edited state when we get new venue information
      if (lastAISuggestionRef.current !== applicationVenueLocation.venue_name) {
        setUserHasEdited(false);
      }

      // If we're using a custom venue, populate all the venue fields
      if (!foundVenue && applicationVenueLocation && !userHasEdited) {
        form.setFieldValue("venue", customVenueKeyword);
        form.setFieldValue(
          "venue_name",
          applicationVenueLocation.venue_name || ""
        );
        form.setFieldValue(
          "venue_address",
          applicationVenueLocation.venue_address || ""
        );
        form.setFieldValue(
          "venue_city",
          applicationVenueLocation.venue_city || ""
        );
        form.setFieldValue(
          "venue_description",
          applicationVenueLocation.venue_description || ""
        );
        form.setFieldValue(
          "venue_phone",
          applicationVenueLocation.venue_phone || ""
        );
        form.setFieldValue(
          "venue_website",
          applicationVenueLocation.venue_website || ""
        );
        form.setFieldValue(
          "venue_location",
          applicationVenueLocation.venue_location || ""
        );

        setInitialSetupDone(true);
      }
    }
  }, [
    applicationVenueLocation,
    foundVenue,
    form,
    customVenueKeyword,
    userHasEdited,
    initialSetupDone,
    disableAiMatch,
  ]);

  return (
    <>
      <Select
        mt="md"
        label={t("venues.title")}
        // Deduplicated: venue names are not unique across towns (33.3
        // Pitfall P2, e.g. two "Igreja Matriz"), and Mantine's Select throws
        // on a duplicate option, which would take the whole form down.
        data={Array.from(
          new Set([
            customVenueKeyword,
            ...availableVenues.map((venue) => venue.name!),
          ])
        )}
        {...form.getInputProps("venue")}
        searchable
        w={400}
        error={form.errors.venue}
        onChange={(value) => {
          form.setFieldValue("venue", value || "");
          setUserHasEdited(true);

          // Reset found venue when user manually changes selection
          if (foundVenue) {
            setFoundVenue(null);
          }
        }}
        styles={
          foundVenue &&
          applicationVenueLocation &&
          foundVenue.name?.toLowerCase() !==
            applicationVenueLocation.venue_name?.toLowerCase()
            ? { input: { border: "2px solid orange", borderRadius: 4 } }
            : undefined
        }
      />
      {venueAssist?.hint && (
        <Badge size="sm" fz={12} variant="light" color="yellow" mt="xs">
          {venueAssist.hint}
        </Badge>
      )}
      {venueAssist?.remember && (
        <Checkbox
          mt="xs"
          label={venueAssist.remember.label}
          checked={venueAssist.remember.checked}
          onChange={(e) =>
            venueAssist.remember?.onChange(e.currentTarget.checked)
          }
        />
      )}
      {findingVenue && (
        <div style={{ marginTop: 5, fontSize: "0.8rem", color: "gray" }}>
          Searching for matching venue...
        </div>
      )}
      {applicationVenueLocation?.venue_name &&
        form.values.venue &&
        applicationVenueLocation.venue_name.toLowerCase().trim() !==
          form.values.venue.toLowerCase().trim() && (
          <div style={{ marginTop: 5, fontSize: "0.8rem", color: "gray" }}>
            ({applicationVenueLocation.venue_name})
          </div>
        )}
      {form.values.venue === customVenueKeyword && (
        <>
          <TextInput
            mt="md"
            label={t("venues.name")}
            {...form.getInputProps("venue_name")}
            w={400}
            onPaste={(e) => {
              handlePaste(e, "venue_name" as keyof EventFormValues);
            }}
            onChange={(e) => {
              setUserHasEdited(true);
              form.setFieldValue("venue_name", e.currentTarget.value);
            }}
            withAsterisk={form.values.published}
          />
          {onSaveAsVenue && (
            <Button
              variant="light"
              size="xs"
              mt="xs"
              type="button"
              onClick={onSaveAsVenue}
            >
              {t("desk.venue_location.save_as_venue")}
            </Button>
          )}
          {onUseVenue && similarVenues.length > 0 && (
            <Stack gap={4} mt="xs">
              <Text size="xs" c="dimmed">
                {t("desk.venue_location.similar_listed")}
              </Text>
              <Group gap="xs">
                {similarVenues.map((venue) => (
                  <Button
                    key={venue.id}
                    variant="light"
                    size="xs"
                    type="button"
                    onClick={() => onUseVenue(venue.id)}
                  >
                    {t("desk.venue_location.use_listed", { name: venue.name })}
                  </Button>
                ))}
              </Group>
            </Stack>
          )}
          {applicationVenueLocation?.venue_name &&
            form.values.venue_name &&
            (!form.values.venue_location ||
              form.values.venue_location.trim() === "") && (
              <Group mt="xs" ml="md">
                <Anchor
                  href={`https://www.google.com/maps/search/${encodeURIComponent(
                    form.values.venue_name
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  style={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  <IconMapSearch size={14} />
                  <Text size="sm">{t("venues.search_on_maps")}</Text>
                </Anchor>
              </Group>
            )}
          <TextInput
            mt="md"
            label={t("venues.address")}
            {...form.getInputProps("venue_address")}
            w={400}
            onPaste={(e) => {
              handlePaste(e, "venue_address" as keyof EventFormValues);
            }}
            onChange={(e) => {
              setUserHasEdited(true);
              form.setFieldValue("venue_address", e.currentTarget.value);
            }}
          />
          <CitySelect
            mt="md"
            label={t("venues.city")}
            cityId={form.values.city_id}
            customCity={form.values.venue_city}
            onChange={({ cityId, customCity }) => {
              setUserHasEdited(true);
              form.setFieldValue("city_id", cityId);
              form.setFieldValue("venue_city", customCity);
            }}
            error={form.errors.venue_city}
            withAsterisk={form.values.published}
            w={400}
          />
          <TextInput
            mt="md"
            label={t("venues.description")}
            {...form.getInputProps("venue_description")}
            w={400}
            onPaste={(e) => {
              handlePaste(e, "venue_description" as keyof EventFormValues);
            }}
            onChange={(e) => {
              setUserHasEdited(true);
              form.setFieldValue("venue_description", e.currentTarget.value);
            }}
          />
          <TextInput
            mt="md"
            label={t("venues.phone")}
            {...form.getInputProps("venue_phone")}
            w={400}
            onPaste={(e) => {
              handlePaste(e, "venue_phone" as keyof EventFormValues);
            }}
            onChange={(e) => {
              setUserHasEdited(true);
              form.setFieldValue("venue_phone", e.currentTarget.value);
            }}
          />
          <TextInput
            mt="md"
            label={t("venues.website")}
            {...form.getInputProps("venue_website")}
            w={400}
            onPaste={(e) => {
              handlePaste(e, "venue_website" as keyof EventFormValues);
            }}
            onChange={(e) => {
              setUserHasEdited(true);
              form.setFieldValue("venue_website", e.currentTarget.value);
            }}
          />
          <Group align="center" mt="md" gap="xs">
            <TextInput
              label={t("venues.location")}
              {...form.getInputProps("venue_location")}
              w={400}
              withAsterisk={form.values.published}
              onChange={(e) => {
                setUserHasEdited(true);
                form.setFieldValue("venue_location", e.currentTarget.value);
              }}
            />
            <ActionIcon
              variant="light"
              radius="xl"
              onClick={() => setShowLocationHelp(true)}
              mt={25}
              color="gray"
              size="md"
            >
              <IconQuestionMark size={16} />
            </ActionIcon>
          </Group>
          {locationNote && (
            <Text size="xs" c="dimmed" mt={4}>
              {locationNote}
            </Text>
          )}
          {form.values.venue_location &&
            form.values.venue_location.includes("http") && (
              <Group mt="xs" ml="md">
                <Anchor
                  href={
                    form.values.venue_location.match(
                      /(https?:\/\/[^\s]+)/g
                    )?.[0] || ""
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  style={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  <IconExternalLink size={14} />
                  <Text size="sm">{t("venues.open_location_link")}</Text>
                </Anchor>
              </Group>
            )}
          <Group mt="md" w={400} h={300}>
            <Map
              zoom={12}
              center={
                isLocationFormatValid(form.values.venue_location)
                  ? {
                      lat: Number(form.values.venue_location.split(",")[0]),
                      lng: Number(form.values.venue_location.split(",")[1]),
                    }
                  : mapFallbackCenter ?? DEFAULT_MAP_CENTER
              }
              gestureHandling={"greedy"}
              disableDefaultUI={true}
            >
              {isLocationFormatValid(form.values.venue_location) && (
                <Marker
                  position={{
                    lat: Number(form.values.venue_location.split(",")[0]),
                    lng: Number(form.values.venue_location.split(",")[1]),
                  }}
                  draggable
                  onDragEnd={(e) => {
                    setUserHasEdited(true);
                    form.setFieldValue(
                      "venue_location",
                      `${e.latLng!.lat()},${e.latLng!.lng()}`
                    );
                  }}
                />
              )}
            </Map>
          </Group>
        </>
      )}
      <Modal
        opened={showLocationHelp}
        onClose={() => setShowLocationHelp(false)}
        size="md"
        centered
      >
        <Stack align="center">
          <img
            src="/gifs/location.gif"
            alt="Location selection help"
            style={{ maxWidth: "100%", marginBottom: "1rem" }}
          />
          <Center mt="xl">
            <Stack ta="center">
              <p>{t("venues.location_help.title")}</p>
              <ol style={{ textAlign: "left" }}>
                <li>
                  {t("venues.location_help.step1")}{" "}
                  <a
                    href="https://www.google.com/maps"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Google Map
                  </a>
                </li>
                <li>{t("venues.location_help.step2")}</li>
                <li>{t("venues.location_help.step3")}</li>
                <li>{t("venues.location_help.step4")}</li>
              </ol>
            </Stack>
          </Center>
          <Button onClick={() => setShowLocationHelp(false)} mt="xl">
            {t("common.close")}
          </Button>
        </Stack>
      </Modal>
    </>
  );
};
